import { Stack, SxProps, Theme } from '@mui/material';
import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import * as React from 'react';
import { usePrevious } from 'react-use';

import {
  Button,
  ButtonProps,
} from '@/components/form/baseInputs/Button/Button';
import { Inbox01Icon } from '@/components/icons/Inbox01Icon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { StyledCounter } from '@/components/notifications/StyledCounter/StyledCounter';

export interface InboxButtonProps
  extends Omit<ButtonProps, 'startIcon' | 'variant' | 'size' | 'children'> {
  /**
   * Variant of the button
   * @default 'dark'
   */
  variant?: 'dark' | 'light';
  /**
   * Whether to show the inbox icon
   * @default true
   */
  showIcon?: boolean;
  /**
   * Custom sx props for the button
   */
  sx?: SxProps<Theme>;
  /**
   * Custom label text for the button
   * @default 'Inbox'
   */
  label?: string;
  /**
   * Notification count to display in the badge
   * If provided, shows a notification badge with the count
   */
  notificationCount?: number;
  /**
   * Whether to show "New" text when there's a notification
   * @default false
   */
  showNewText?: boolean;
}

export const InboxButton: React.FC<InboxButtonProps> = ({
  variant: _variant = 'dark',
  showIcon = true,
  label = 'Inbox',
  notificationCount,
  showNewText = false,
  sx,
  ...props
}) => {
  const hasNotification =
    notificationCount !== undefined && notificationCount > 0;

  const [shouldPulse, setShouldPulse] = useState(false);
  const prevHasNewText = usePrevious(showNewText);

  // Track when "New" badge appears after not being present
  useEffect(() => {
    if (showNewText && prevHasNewText === false) {
      setShouldPulse(true);
      // Stop pulsing after 3 pulses (approximately 2 seconds)
      const timer = setTimeout(() => setShouldPulse(false), 2000);
      return () => clearTimeout(timer);
    }

    // returning this for return format consistency
    return noop;
  }, [showNewText, prevHasNewText]);

  const buttonContent = (
    <Button
      variant="secondary"
      size="xs"
      startIcon={showIcon ? Inbox01Icon : undefined}
      sx={{
        minWidth: 'auto',
        ...sx,
      }}
      {...props}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <span>{label}</span>
        {showNewText && (
          <Badge
            variant={BadgeVariants.Destructive}
            display="New"
            sx={{
              pt: 0,
              pb: 0,
              height: '16px',
              lineHeight: '1rem',
              display: 'inline-block',
              ...(shouldPulse && {
                animation: 'pulse 0.6s ease-in-out 3',
              }),
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                },
                '50%': {
                  transform: 'scale(1.1)',
                },
                '100%': {
                  transform: 'scale(1)',
                },
              },
            }}
          />
        )}
      </Stack>
    </Button>
  );

  if (hasNotification) {
    return (
      <StyledCounter
        badgeContent={notificationCount}
        color="error"
        show={true}
        sx={{
          '& .MuiBadge-badge': {
            right: 8,
            top: 8,
          },
        }}
      >
        {buttonContent}
      </StyledCounter>
    );
  }

  return buttonContent;
};
